import { supabase } from './supabase';
import { expectedError } from './report-error';

export type R2Folder =
  | 'covers'
  | 'avatars'
  | 'watermarks'
  | 'properties'
  | 'project-covers'
  | 'logos'
  | 'blog'
  | 'agency-assets'
  | 'documents';

interface PresignResponse {
  uploadUrl: string;
  publicUrl: string;
  key: string;
  expiresIn: number;
}

export interface UploadImageOptions {
  folder: R2Folder;
  subfolder?: string;
  filename?: string;
  /**
   * When true, also produce a small 800×420 JPEG and upload it as a sibling
   * file with `-og.jpg` suffix. The returned `ogThumbUrl` should be stored on
   * `property_photos.og_thumb_url` for fast WhatsApp/FB link previews via the
   * vitrina-preview edge function. Only set this for cover photos.
   */
  generateOgThumb?: boolean;
}

export interface UploadImageResult {
  url: string;
  key: string;
  /** Set when generateOgThumb=true and resize+upload succeeded. */
  ogThumbUrl?: string;
}

const OG_THUMB_W = 800;
const OG_THUMB_H = 420;
const OG_THUMB_QUALITY = 0.85;

/**
 * What to do after a failed `storage-presign` call.
 *
 * A 401 partway through a bulk upload means the access token expired between
 * photos, not that the user is signed out: the earlier photos in the same batch
 * succeeded. Treating it as a deterministic 4xx (as every other 4xx correctly
 * is) silently dropped the tail of the batch — GlitchTip intver2-web #1119, 29
 * events, always on the 12th photo onward. Refresh once, then retry.
 *
 * ponytail: extracted as a pure function so the policy is testable without
 * mocking the supabase client.
 */
export function presignRetryAction(
  status: number | undefined,
  alreadyRefreshed: boolean,
): 'refresh' | 'retry' | 'give-up' {
  if (status === 401 && !alreadyRefreshed) return 'refresh';
  if (status !== undefined && status >= 400 && status < 500) return 'give-up';
  return 'retry';
}

function deriveOgThumbFilename(originalFilename: string): string {
  const dot = originalFilename.lastIndexOf('.');
  const base = dot > 0 ? originalFilename.slice(0, dot) : originalFilename;
  return `${base}-og.jpg`;
}

// Center-crop + resize to 800×420 JPEG via canvas. The "cover" semantics here
// match weserv's `fit=cover` so pre-generated thumbs visually match the
// fallback path (weserv) for legacy photos.
async function resizeToOgThumb(file: Blob): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const canvas = document.createElement('canvas');
      canvas.width = OG_THUMB_W;
      canvas.height = OG_THUMB_H;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('canvas 2d context unavailable'));
        return;
      }
      ctx.imageSmoothingQuality = 'high';
      const srcRatio = img.width / img.height;
      const dstRatio = OG_THUMB_W / OG_THUMB_H;
      let sx = 0, sy = 0, sw = img.width, sh = img.height;
      if (srcRatio > dstRatio) {
        sw = img.height * dstRatio;
        sx = (img.width - sw) / 2;
      } else {
        sh = img.width / dstRatio;
        sy = (img.height - sh) / 2;
      }
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, OG_THUMB_W, OG_THUMB_H);
      canvas.toBlob(
        (blob) => blob ? resolve(blob) : reject(new Error('canvas.toBlob returned null')),
        'image/jpeg',
        OG_THUMB_QUALITY,
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('failed to decode image for og-thumb resize'));
    };
    img.src = objectUrl;
  });
}

/**
 * PUT a blob to a presigned R2 URL.
 *
 * Deliberately XHR and not `fetch`: Safari intermittently kills cross-origin
 * PUTs that carry a Blob body, rejecting with `TypeError: Load failed` — no
 * HTTP response, no CORS error, just a dead request. It's a WebKit networking
 * bug, not ours. GlitchTip intver2-web #118 is 5/5 Safari/Mobile Safari while
 * the CRM's baseline error traffic is ~all Chrome, and it hits mid-batch (14 of
 * 15 photos upload, one dies). XHR takes a different WebKit code path that
 * doesn't have the bug, so every direct-to-R2 upload goes through here.
 */
export function putToR2(url: string, body: Blob, contentType: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    xhr.setRequestHeader('Content-Type', contentType);
    // ponytail: flat timeout. Presigned URLs live 5 min, so anything slower than
    // this was never going to land anyway. Make it size-derived if big documents
    // start tripping it.
    xhr.timeout = 120_000;
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`R2 upload failed: ${xhr.status} ${xhr.statusText}`));
    };
    xhr.onerror = () => reject(new Error('R2 upload failed: error de red'));
    xhr.ontimeout = () => reject(new Error('R2 upload failed: la subida tardó demasiado'));
    xhr.onabort = () => reject(new Error('R2 upload failed: subida cancelada'));
    xhr.send(body);
  });
}

export async function uploadImageToR2(
  file: Blob,
  opts: UploadImageOptions,
): Promise<UploadImageResult> {
  if (!supabase) throw new Error('Supabase client not initialized');

  // Derive filename: explicit opts.filename > File.name > fallback
  const filename = opts.filename
    || (typeof File !== 'undefined' && file instanceof File ? file.name : 'upload');
  const contentType = file.type || 'application/octet-stream';

  const presignBody = {
    folder: opts.folder,
    subfolder: opts.subfolder,
    filename,
    contentType,
    size: file.size,
  };

  // storage-presign occasionally fails transiently mid bulk-upload (function
  // cold start, mobile network blip) — a single blip shouldn't drop the whole
  // image. The presign is idempotent, so retry a few times with backoff before
  // giving up. 4xx responses are deterministic rejections (413 too large, 400
  // bad content-type) — retrying them is pointless, so bail on the first one.
  // Tracked: GlitchTip intver2-web #144 / #197 / #787.
  type PresignError = { message?: string; context?: Response };
  let data: PresignResponse | null = null;
  let lastErr: PresignError | null = null;
  let lastStatus: number | undefined;
  let refreshed = false;
  let attempt = 0;
  while (attempt < 3) {
    const res = await supabase.functions.invoke<PresignResponse>('storage-presign', { body: presignBody });
    if (res.data && !res.error) {
      data = res.data;
      break;
    }
    lastErr = res.error as PresignError;
    lastStatus = (lastErr?.context as Response | undefined)?.status;
    const action = presignRetryAction(lastStatus, refreshed);
    if (action === 'give-up') break;
    if (action === 'refresh') {
      // Expired token, not a signed-out user (see presignRetryAction). Refresh
      // and retry the same photo; this does not consume a transient attempt,
      // so a token that expires on the last retry still gets its one chance.
      refreshed = true;
      const { error: refreshErr } = await supabase.auth.refreshSession();
      if (refreshErr) break; // genuinely signed out — surface the 401
      continue;
    }
    attempt++;
    if (attempt < 3) await new Promise((r) => setTimeout(r, 300 * attempt));
  }

  if (!data) {
    // supabase-js's generic "non-2xx status code" hides the WHY; the function
    // returns { error: '...' } JSON with the real reason (size cap, bad type).
    let fnMessage = '';
    try {
      fnMessage = (await (lastErr?.context as Response | undefined)?.json())?.error ?? '';
    } catch { /* body unavailable/already consumed */ }
    if (lastStatus === 413) {
      // The user picked a file that is simply too big — their problem to fix,
      // not a defect. expectedError keeps the alert but keeps it out of the tracker.
      throw expectedError(`La imagen supera el límite de 15 MB (${Math.round(file.size / 1024 / 1024)} MB). Reduce su tamaño e inténtalo de nuevo.`);
    }
    throw new Error(
      [fnMessage || lastErr?.message || 'Failed to get presigned URL', lastStatus ? `(HTTP ${lastStatus})` : '']
        .filter(Boolean).join(' '),
    );
  }

  await putToR2(data.uploadUrl, file, contentType);

  const result: UploadImageResult = { url: data.publicUrl, key: data.key };

  if (opts.generateOgThumb) {
    try {
      const thumbBlob = await resizeToOgThumb(file);
      const thumbResult = await uploadImageToR2(thumbBlob, {
        folder: opts.folder,
        subfolder: opts.subfolder,
        filename: deriveOgThumbFilename(filename),
        generateOgThumb: false,
      });
      result.ogThumbUrl = thumbResult.url;
    } catch (err) {
      // Thumb generation is best-effort. If it fails (corrupt image, browser
      // unsupported codec, etc.) the original upload still succeeds; the edge
      // function falls back to weserv proxying the full image.
      console.warn('og-thumb generation failed:', err);
    }
  }

  return result;
}
